import React from 'react';
import { Link } from 'react-router-dom';
import {
  getImeiUnitHistoryEvents,
  formatInr,
  formatHistoryDate
} from '../../utils/imeiHelpers';

/**
 * Compact timeline: intake seller → past sales → buybacks → current sale (if sold).
 */
export default function ImeiUnitHistoryPanel({ imei, compact = false }) {
  const pastEvents = getImeiUnitHistoryEvents(imei);
  const currentSale =
    imei?.status === 'sold' && imei?.soldTo
      ? { type: 'sale', ...imei.soldTo, _current: true }
      : null;

  const intakeName = String(imei?.unitSeller?.sellerName || '').trim();
  const intakePhone = String(imei?.unitSeller?.sellerPhone || '').trim();
  const hasIntake = Boolean(intakeName || intakePhone);
  const hasPast = pastEvents.length > 0;
  const hasCurrent = Boolean(currentSale);

  if (!hasIntake && !hasPast && !hasCurrent) {
    return compact ? null : <span className="text-muted">—</span>;
  }

  const renderSale = (ev, key) => {
    const name = ev.customerName || (ev.isManualAdjustment ? 'Manual' : '—');
    const phone = ev.customerPhone || '';
    const emiId = ev.emiId && typeof ev.emiId === 'object' ? ev.emiId._id ?? ev.emiId : ev.emiId;
    return (
      <li key={key} className="small mb-1">
        <span className="badge bg-danger me-1">Sold</span>
        <strong>{name}</strong>
        {phone ? <span className="text-muted"> · {phone}</span> : null}
        {ev.salePrice != null ? <> · {formatInr(ev.salePrice)}</> : null}
        {formatHistoryDate(ev) ? (
          <span className="text-muted"> · {formatHistoryDate(ev)}</span>
        ) : null}
        {ev._current ? (
          <span className="badge bg-warning text-dark ms-1">Current</span>
        ) : null}
        {emiId ? (
          <>
            {' '}
            <Link to={`/emi/${encodeURIComponent(String(emiId))}`} className="ms-1">
              EMI
            </Link>
          </>
        ) : null}
      </li>
    );
  };

  const renderBuyback = (ev, key) => (
    <li key={key} className="small mb-1">
      <span className="badge bg-info text-dark me-1">Buyback</span>
      {ev.customerName ? <strong>{ev.customerName}</strong> : null}
      {ev.customerPhone ? (
        <span className="text-muted">
          {ev.customerName ? ' · ' : ''}
          {ev.customerPhone}
        </span>
      ) : null}
      {ev.price != null ? <> · {formatInr(ev.price)}</> : null}
      {formatHistoryDate(ev) ? (
        <span className="text-muted"> · {formatHistoryDate(ev)}</span>
      ) : null}
      {ev._legacyOnly ? (
        <span className="text-muted ms-1" title="Purani entry — pehli sale detail save nahi thi">
          (purana record)
        </span>
      ) : null}
    </li>
  );

  const body = (
    <ul className="list-unstyled mb-0 ps-0">
      {hasIntake && (
        <li className="small mb-1 text-muted">
          <span className="badge bg-secondary me-1">Intake</span>
          {intakeName || '—'}
          {intakePhone ? ` · ${intakePhone}` : ''}
          {imei.addedDate ? (
            <span> · added {new Date(imei.addedDate).toLocaleDateString('en-IN')}</span>
          ) : null}
        </li>
      )}
      {pastEvents.map((ev, i) =>
        ev.type === 'buyback' ? renderBuyback(ev, `b-${i}`) : renderSale(ev, `s-${i}`)
      )}
      {hasCurrent && renderSale(currentSale, 'current')}
    </ul>
  );

  if (compact) {
    const count = pastEvents.length + (hasCurrent ? 1 : 0);
    if (count === 0 && !hasIntake) return null;
    return (
      <details className="imei-unit-history-compact">
        <summary className="small text-primary" style={{ cursor: 'pointer' }}>
          History ({count || 1})
        </summary>
        <div className="mt-1 border-start border-2 border-secondary ps-2">{body}</div>
      </details>
    );
  }

  return (
    <div className="imei-unit-history border rounded p-2 bg-light">
      <div className="small fw-semibold mb-1">
        <i className="bi bi-clock-history me-1"></i>
        Unit history
      </div>
      {body}
    </div>
  );
}
